import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Send, Lock } from "lucide-react";
import { useLang } from "../contexts/LangContext";

const COMPANY = {
  name: "Scudieri Group S.r.l.",
  address: "Viale Lunigiana 8, 20125 Milano",
  vat: "09395210967",
  rea: "MI-2087451",
  email: "scudierigroup@gmail.com",
  pec: "sushicompanysrl1@legalmail.it",
};

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#0a0a0a] border-t border-white/10 mt-0" data-testid="site-footer">
      <div className="container-x py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="mb-5">
            <img
              src="/logo.png"
              alt="Scudieri Group"
              className="h-28 w-auto object-contain"
              data-testid="footer-logo"
            />
          </div>
          <p className="text-[color:var(--txt-secondary)] max-w-md leading-relaxed">
            {t.footer.tagline}
          </p>
          <div className="mt-6 space-y-2 text-sm text-[color:var(--txt-secondary)]">
            <div className="flex items-start gap-3" data-testid="footer-address">
              <MapPin className="w-4 h-4 text-[color:var(--gold)] mt-0.5 shrink-0" />
              <span>{COMPANY.address}</span>
            </div>
            <div className="flex items-start gap-3" data-testid="footer-email">
              <Mail className="w-4 h-4 text-[color:var(--gold)] mt-0.5 shrink-0" />
              <a href={`mailto:${COMPANY.email}`} className="link-gold">
                {COMPANY.email}
              </a>
            </div>
            <div className="flex items-start gap-3" data-testid="footer-pec">
              <Send className="w-4 h-4 text-[color:var(--gold)] mt-0.5 shrink-0" />
              <span>PEC: {COMPANY.pec}</span>
            </div>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="eyebrow mb-5">{t.footer.sitemap}</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/" className="link-gold">{t.nav.home}</Link></li>
            <li><Link to="/format" className="link-gold">{t.nav.formats}</Link></li>
            <li><Link to="/servizi" className="link-gold">{t.nav.services}</Link></li>
            <li><Link to="/eventi" className="link-gold">{t.nav.events}</Link></li>
            <li><Link to="/galleria" className="link-gold">{t.nav.gallery}</Link></li>
            <li><Link to="/contatti" className="link-gold">{t.nav.contact}</Link></li>
            <li><Link to="/lavora-con-noi" className="link-gold">{t.nav.careers}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="eyebrow mb-5">{t.footer.legal}</h4>
          <ul className="space-y-3 text-sm">
            <li>
              <span className="text-[color:var(--txt-muted)]">P.IVA / CF: </span>
              <span className="text-white">{COMPANY.vat}</span>
            </li>
            <li>
              <span className="text-[color:var(--txt-muted)]">REA: </span>
              <span className="text-white">{COMPANY.rea}</span>
            </li>
            <li><Link to="/privacy" className="link-gold">{t.footer.privacy}</Link></li>
            <li><Link to="/cookie" className="link-gold">{t.footer.cookie}</Link></li>
            <li>
              <button
                type="button"
                onClick={() => window.__openCookieSettings && window.__openCookieSettings()}
                className="link-gold text-left"
                data-testid="footer-cookie-settings"
              >
                {(t.footer.cookieSettings || "Gestisci cookie")}
              </button>
            </li>
            <li><Link to="/admin/login" className="link-gold text-[color:var(--txt-muted)]" data-testid="admin-login-link">{t.nav.adminLogin}</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[color:var(--txt-muted)]">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 order-2 md:order-1">
            <a
              href="https://erp.scudieri-group.it"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[color:var(--gold)]/40 text-[color:var(--gold)] hover:bg-[color:var(--gold)] hover:text-black font-display tracking-[0.22em] uppercase text-[10px] transition-colors"
              data-testid="footer-erp-link"
            >
              <Lock className="w-3.5 h-3.5" />
              Area Riservata
            </a>
            <span data-testid="footer-copy">
              © {year} {COMPANY.name}. {t.footer.copy}
            </span>
          </div>
          <div className="font-display tracking-[0.32em] uppercase text-[10px] order-1 md:order-2">
            Milano · Italia
          </div>
        </div>
      </div>
    </footer>
  );
}
